import { css, html, LitElement, PropertyValues, render } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../styles/shared";
import { property } from "lit/decorators/property.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { replaceIdeaRefsInText } from "../molecules/idea-tooltip";
import { Size } from "shared";
import markdown from "markdown-it";
import markdownItMark from "markdown-it-mark";
import MarkdownIt from "markdown-it/lib";
import { stopEvent } from "../util/util";
import { openDialog, WebDialog } from "web-dialog";

const MARKDOWN_PARSER = markdown({
	html: false,
	linkify: true,
	typographer: true
}).use(markdownItMark);

@customElement("bs-markdown")
export class BsMarkdown extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					display: block;
					font-size: var(--markdown-font-size, var(--font-size-m));
					line-height: var(--markdown-line-height, 1.3);
				}

				:host([size="large"]) {
					font-size: var(--font-size-l);
					line-height: var(--markdown-line-height, 1.5);
					--block-margin-bottom: var(--space-l);
					--markdown-title-margin: 0 0 0.2em;
				}

				h1,
				h2,
				h3,
				h4,
				h5,
				h6 {
					font-weight: bold;
					line-height: inherit;
					margin: var(--markdown-title-margin, 0 0 0.1em);
					page-break-after: avoid;
				}

				p {
					white-space: break-spaces;
				}

				h1 {
					font-size: var(--markdown-h1-font-size, 1.3em);
				}

				h2 {
					font-size: var(--markdown-h2-font-size, 1.15em);
				}

				h3 {
					font-size: var(--markdown-h3-font-size, 1em);
				}

				h4,
				h5,
				h6 {
					font-size: var(--markdown-h4-font-size, 0.85em);
				}

				p,
				ul,
				ol,
				li {
					line-height: inherit;
					margin: 0;
				}

				li p,
				ol p,
				li li {
					margin-bottom: 0 !important;
				}

				a {
					color: var(--markdown-link-color, -webkit-link);
				}

				ul,
				ol {
					padding-left: var(--space-l);
				}

				table:not(:last-child),
				p:not(:last-child),
				ol:not(:last-child),
				blockquote:not(:last-child),
				pre:not(:last-child),
				ul:not(:last-child) {
					margin-bottom: var(--block-margin-bottom, var(--space-m));
				}

				li:not(:last-child) {
					margin-bottom: var(--space-s);
				}

				img {
					width: var(--markdown-img-width, 100%);
					page-break-before: avoid;
					display: var(--markdown-img-display, initial);
					border: var(--markdown-img-border);
					max-width: var(--markdown-img-max-width, 60%);
					margin: var(--markdown-img-margin, initial);
					border-radius: var(--markdown-img-border-radius, var(--border-radius-s));
					cursor: zoom-in;
				}

				img[alt*="border"] {
					border: var(--markdown-img-border, 1px solid var(--shade-normal));
				}

				img[alt*="w20"] {
					width: var(--markdown-img-width, 20%);
				}

				img[alt*="w30"] {
					width: var(--markdown-img-width, 30%);
				}

				img[alt*="w40"] {
					width: var(--markdown-img-width, 40%);
				}

				img[alt*="w50"] {
					width: var(--markdown-img-width, 50%);
				}

				img[alt*="shadow"] {
					box-shadow: 0 2px 15px -5px var(--shadow);
				}

				table {
					width: 100%;
					border-collapse: collapse;
				}

				th {
					background: var(--shade-light);
					color: var(--shade-contrast-light);
				}

				table,
				th,
				td {
					border: 1px solid currentColor;
				}

				th,
				td {
					padding: var(--space-s);
				}

				code {
					padding: var(--space-xxs) var(--space-s);
					font-size: 0.9em;
					background: var(--red-contrast-dark);
					color: var(--red-dark);
					border-radius: var(--border-radius-s);
				}

				pre {
					display: block;
					padding: var(--space-m);
					background: var(--red-contrast-dark);
					border: 1px solid currentColor;
					border-radius: var(--border-radius-m);
					overflow: auto;
					max-height: 600px;
				}

				blockquote {
					padding: var(--space-s) var(--space-m);
					border-left: 5px solid var(--primary-normal);
					margin: 0;
				}
			`
		];
	}

	@property({ type: Object }) parser: MarkdownIt = MARKDOWN_PARSER;
	@property({ type: String, reflect: false }) text?: string;
	@property({ type: Boolean, reflect: false, attribute: "idea-refs" }) ideaRefs = false;
	@property({ type: String, reflect: true }) size: Size = "medium";

	private boundHandleClick = this.handleClick.bind(this);

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		this.addEventListener("click", this.boundHandleClick);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener("click", this.boundHandleClick);
	}

	handleClick(e: Event) {
		// Check if the image is clicked and zoomable
		const clickedTarget = e.composedPath()?.[0] ?? e.target;
		if (clickedTarget instanceof HTMLImageElement) {
			const url = clickedTarget.src;
			if (url != null) {
				stopEvent(e);
				openDialog({
					$content: ($dialog) => render(html`<img style="width: 100%;" src="${url}" />`, $dialog),
					initialize: () => {
						const $dialog = new WebDialog();
						$dialog.style.setProperty("--dialog-max-width", "1200px");
						return $dialog;
					}
				});
				return;
			}
		}
	}

	render() {
		const text = this.parser.render(this.text ?? "");
		return html`${unsafeHTML(text && this.ideaRefs ? replaceIdeaRefsInText({ text }) : text)}`;
	}
}
